import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Newspaper, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { FaqAccordion } from "@/components/FaqAccordion";
import { API } from "@/lib/newsUtils";
import { useSeo } from "@/lib/seo";

export default function Page({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading");

  useSeo({
    title: page ? (page.meta_title || `${page.title} | News Digest`) : "News Digest",
    description: page?.meta_description || (page ? `${page.title} · News Digest, tus medios resumidos con IA en un solo feed.` : undefined),
  });

  useEffect(() => {
    setStatus("loading");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/${slug}`);
        setPage(data);
        setStatus("done");
      } catch {
        setStatus("error");
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="content-page">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-lg font-black tracking-tight hidden sm:inline">NEWS DIGEST</span>
          </Link>
          <Button
            data-testid="page-back-button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-none border-border h-10 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-12">
        {status === "loading" && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
          </div>
        )}
        {status === "error" && (
          <p className="text-muted-foreground" data-testid="page-not-found">Esta página no existe.</p>
        )}
        {status === "done" && page && (
          <article>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-8" data-testid="page-title">
              {page.title}
            </h1>
            <div
              data-testid="page-content"
              className="prose prose-neutral max-w-none prose-headings:font-heading prose-a:text-primary"
              dangerouslySetInnerHTML={{ __html: page.html }}
            />
          </article>
        )}
      </main>

      {status === "done" && page && <FaqAccordion sections={page.sections} />}

      <Footer />
    </div>
  );
}
